import React, { useEffect, useState } from 'react'
import Module from '../../../../layout/Module'
import { Box, Step, StepLabel, Stepper } from '@mui/material'
import { useDispatch, useSelector } from 'react-redux'
import { useNavigate, useParams } from 'react-router-dom'
import ButtonStartIcon from '../../../../button/ButtonStartIcon'
import FormCurriculumElements from '../FormCurriculumElements/FormCurriculumElements'
import FormLcElements from '../FormLcElements/FormLcElements'
import FormLevels from '../FormLevels/FormLevels'
import FormPreview from '../FormPreview/FormPreview'
import useLearningContinuum from '../../../../../hooks/learning-continuum/useLearningContinuum'
import { Alert } from '@mui/material'
import AlertSaveButton from '../../../../button/AlertSaveButton'
import isEmpty from 'lodash/isEmpty'
import { validateCurriculumElements, validateLcElements, validateLevels } from '../../utils'
import { toggleAlertModule } from '../../../../../store/actions/alert.actions'
import { updateLearningContinuumFormField } from '../../../../../store/actions/learning-continuum.actions'
import IconTooltipButton from '../../../../button/IconTooltipButton'

const Create = () => {
  const { createLearningContinua } = useLearningContinuum()
  const dispatch = useDispatch()
  const steps = ['Basic Information', 'Elements', 'Levels', 'Preview'];
  const formData = useSelector((state) => state?.lc?.form || {});
  const currentCurriculum = useSelector((state) => state?.assessment?.currentCurriculum || {})
  const alertSave = useSelector(state => state?.alert?.save || {})

  const permission = useSelector((state) => state?.session?.permissions?.lc || {})
  const { curriculumId, step } = useParams()
  const [error, setError] = useState(null)
  const navigate = useNavigate()

  const [loading, toggleLoading] = useState(false)

  const onCancel = () => {
    navigate(`/dashboard/learning-continuum/${curriculumId}`)
    dispatch(toggleAlertModule(null))
  }
  const onRedirect = (step) => {
    navigate(`/dashboard/learning-continuum/${curriculumId}/create/step/${step}`)
  }
  const onChangeAlert = (key, value) => {
    dispatch(updateLearningContinuumFormField(key, value))
    if (!alertSave.mode || !alertSave.onSave) {
      dispatch(toggleAlertModule(false, 'Learning Continuum', 'create', onSave, alertSave.onDontSave))
    }
  }

  const onNext = () => {
    setError(null)
    let errors = {}

    if (step === '0') {
      errors = validateCurriculumElements(formData.curriculumElements, currentCurriculum)
    }
    if (step === '1') {
      errors = validateLcElements(formData.lcElements, currentCurriculum.lcElements.children)
    }

    if (step === '2') {
      errors = validateLevels(formData.levels, currentCurriculum.levels)
    }
  
    if (isEmpty(errors)) {
      onRedirect(parseInt(step) + 1)
    } else {
      setError(errors)
    }
  }
  const onSave = async (e) => {
    e.preventDefault()
    toggleLoading(true)
    try {
      let result = await createLearningContinua({ ...formData })
      if (result.error) {
        setError(result)
      } else {
        onCancel()
      }
    } finally {
      toggleLoading(false)
    }
  }

  useEffect(() => {
    if (isEmpty(formData)) {
      onCancel()
    }
  }, [formData])

  return (
    <>
      <Module.BodyHeader className='border-b border-b-2 border-primary flex justify-between'>
        <Stepper color='primary' activeStep={parseInt(step)} className='w-full'>
          { steps.map((step, index) => (
            <Step key={`step-${index}`}>
              <StepLabel>{step}</StepLabel>
            </Step>
          ))}
        </Stepper>
        <div className='flex items-center justify-end gap-4'>
          <div className='min-w-25px'>
            { parseInt(step) > 0 &&
              <IconTooltipButton
                disabled={loading}
                title='Previous'
                icon='chevron-left'
                classNameIcon='text-lg'
                style={{
                  borderRadius: '0.5rem',
                  padding: '0.45rem',
                  color: 'var(--primary)',
                  backgroundColor: '#fff',
                  width: 25,
                  height: 25
                }}
                className="shadow-sm"
                onClick={() => onRedirect(parseInt(step) - 1)}
              />
            }
          </div>
          <div className='min-w-100px'>
            { parseInt(step) === steps.length - 1 ?
              permission.created && <ButtonStartIcon
                disabled={loading}
                loading={loading}
                onClick={onSave}
                startIcon={['fal','check']}
                color='primary'
                className='w-full'
              >
                Save
              </ButtonStartIcon>
              :
              <IconTooltipButton
                disabled={loading}
                title='Next'
                icon='chevron-right'
                classNameIcon='text-lg'
                style={{
                  borderRadius: '0.5rem',
                  padding: '0.45rem',
                  color: 'var(--primary)',
                  backgroundColor: '#fff',
                  width: 25,
                  height: 25
                }}
                className="shadow-sm"
                onClick={onNext}
              />
            }
          </div>
          <AlertSaveButton
            submodule='Learning Continuum'
            disabled={loading}
            icon={['fal', 'times']}
            title='Cancel'
            color='primary'
            variant='outlined'
            className='shadow-sm  w-full'
            onDontSave={onCancel}
          />
        </div>
      </Module.BodyHeader>
      <Module.BodyContainer 
        loading={loading }
        classNameWrapper={loading ?  '' : 'overflow-auto' }
      >
        <Box className={[
          'absolute inset-0',
          step === '3' ? '' : 'pb-2 pt-6 px-4 ',
        ].join(' ')}>
          { error?.message &&
            <Alert
              severity='error' 
              className='mb-4'
              onClose={() => setError(null)}
            >
              {error?.message}
            </Alert> 
          }
          { step === '0' && <FormCurriculumElements onChangeAlert={onChangeAlert} />}
          { step === '1' && <FormLcElements onChangeAlert={onChangeAlert} />}
          { step === '2' && <FormLevels onChangeAlert={onChangeAlert} />}
          { step === '3' && <FormPreview className=' '/>}
        </Box>
      </Module.BodyContainer>
    </>
  )
}

export default Create